import { types as api } from '@mesa-labs/mesa-api';
import { DateTime } from 'luxon';
import { matchPath } from 'react-router-dom';

import { comparePartnerIds, matchesPartnerFilter, primaryProgram } from './actionItemPartners';
import { formatOperatorEmail } from './permissionUsers';

type StatusMeta = { readonly label: string; readonly colorKey: string };

// Labels plus the mesa-ui StatusLabel color for each status. Waiting is amber (`warning`, no icon) so it reads apart
// from In Progress's blue.
const STATUS_META: Readonly<Record<api.VendorActionItemStatus, StatusMeta>> = {
  [api.VendorActionItemStatus.TODO]: { label: 'To Do', colorKey: 'neutral' },
  [api.VendorActionItemStatus.IN_PROGRESS]: { label: 'In Progress', colorKey: 'info' },
  [api.VendorActionItemStatus.WAITING]: { label: 'Waiting', colorKey: 'warning' },
  [api.VendorActionItemStatus.DONE]: { label: 'Done', colorKey: 'success' },
  [api.VendorActionItemStatus.CANCELED]: { label: 'Canceled', colorKey: 'cancelled' },
};

// Lifecycle order: open statuses, then closed.
export const ActionItemStatuses: readonly api.VendorActionItemStatus[] = [
  ...api.OpenVendorActionItemStatuses,
  ...api.ClosedVendorActionItemStatuses,
];

export const actionItemStatusLabel = (status: string): string => STATUS_META[status as api.VendorActionItemStatus]?.label ?? status;

// Status picker choices in lifecycle order.
export const ActionItemStatusOptions: readonly { readonly label: string; readonly value: api.VendorActionItemStatus }[] = (
  ActionItemStatuses.map((status) => ({ label: STATUS_META[status].label, value: status }))
);

export const actionItemStatusColorKey = (status: api.VendorActionItemStatus): string => STATUS_META[status]?.colorKey ?? 'neutral';

export const isOpenActionItem = (item: Pick<api.VendorActionItemResponse, 'status'>): boolean => api.isOpenVendorActionItemStatus(item.status);

// How long an open item has gone without activity (an edit or a comment), in business days (weekends don't count):
// `warning` after 3, `critical` after 5.
export type ActionItemStaleness = 'warning' | 'critical';

// Counted as business days
export const ActionItemStaleWarningBusinessDays = 3;
export const ActionItemStaleCriticalBusinessDays = 5;

// Amber and red: a card's border, a table row's tint, and the idle age's text.
export const ActionItemStalenessColors: Readonly<Record<ActionItemStaleness, {
  readonly border: string;
  readonly background: string;
  readonly text: string;
}>> = {
  warning: { border: '#FDB022', background: '#FFFAEB', text: '#B54708' },
  critical: { border: '#F97066', background: '#FEF3F2', text: '#B42318' },
};

// Calendar days since the item's last activity (fractional).
const idleDays = (item: Pick<api.VendorActionItemResponse, 'lastActivityAt'>, now: DateTime): number => (
  now.diff(DateTime.fromISO(item.lastActivityAt), 'days').days
);

// Business days since the item's last activity (fractional): the idle time minus any weekend day, in `now`'s zone. The
// weekend is always Saturday and Sunday. Bank holidays still count.
const idleBusinessDays = (item: Pick<api.VendorActionItemResponse, 'lastActivityAt'>, now: DateTime): number => {
  let cursor = DateTime.fromISO(item.lastActivityAt).setZone(now.zone);
  // An unparseable timestamp rates the item fresh rather than throwing mid-render.
  if (!cursor.isValid || cursor >= now) return 0;
  // Each whole week holds five business days, so only the last partial week needs walking.
  const weeks = Math.floor(now.diff(cursor, 'weeks').weeks);
  cursor = cursor.plus({ weeks });
  let days = weeks * 5;
  while (cursor < now) {
    const next = DateTime.min(cursor.plus({ days: 1 }).startOf('day'), now);
    if (cursor.weekday <= 5) days += next.diff(cursor, 'days').days;
    cursor = next;
  }
  return days;
};

// A stale item's rating with the business days it's rated on, so a caller can show both from one walk of the days.
export type ActionItemStaleAge = {
  readonly staleness: ActionItemStaleness;
  readonly businessDays: number;
};

// Closed items are never stale.
export const actionItemStaleAge = (
  item: Pick<api.VendorActionItemResponse, 'status' | 'lastActivityAt'>,
  now: DateTime = DateTime.now(),
): ActionItemStaleAge | undefined => {
  if (!isOpenActionItem(item)) return undefined;
  const businessDays = idleBusinessDays(item, now);
  if (businessDays > ActionItemStaleCriticalBusinessDays) return { staleness: 'critical', businessDays };
  if (businessDays > ActionItemStaleWarningBusinessDays) return { staleness: 'warning', businessDays };
  return undefined;
};

export const actionItemStaleness = (
  item: Pick<api.VendorActionItemResponse, 'status' | 'lastActivityAt'>,
  now?: DateTime,
): ActionItemStaleness | undefined => actionItemStaleAge(item, now)?.staleness;

export const isStaleActionItem = (
  item: Pick<api.VendorActionItemResponse, 'status' | 'lastActivityAt'>,
  now?: DateTime,
): boolean => actionItemStaleness(item, now) !== undefined;

// Any item, open or closed, with activity in the last 3 business days.
export const isRecentActionItem = (item: Pick<api.VendorActionItemResponse, 'lastActivityAt'>, now: DateTime = DateTime.now()): boolean => (
  idleBusinessDays(item, now) <= ActionItemStaleWarningBusinessDays
);

// The board's Age filter: every item, recent ones (open or closed, active in the last 3 business days), or stale ones
// (open only).
// A closed item idle for longer is in neither.
export type ActionItemsAgeFilter = 'all' | 'stale' | 'recent';

export const ActionItemsAgeFilterOptions: readonly { readonly value: ActionItemsAgeFilter; readonly label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'recent', label: 'Recent' },
  { value: 'stale', label: 'Stale' },
];

// The Age filter on an item's already counted idle business days (the same rules as isStaleActionItem and
// isRecentActionItem).
const matchesAgeFilterOn = (
  item: Pick<api.VendorActionItemResponse, 'status'>,
  age: ActionItemsAgeFilter,
  businessDays: number,
): boolean => {
  switch (age) {
    case 'stale':
      return isOpenActionItem(item) && businessDays > ActionItemStaleWarningBusinessDays;
    case 'recent':
      return businessDays <= ActionItemStaleWarningBusinessDays;
    default:
      return true;
  }
};

export const matchesAgeFilter = (
  item: Pick<api.VendorActionItemResponse, 'status' | 'lastActivityAt'>,
  age: ActionItemsAgeFilter,
  now: DateTime = DateTime.now(),
): boolean => age === 'all' || matchesAgeFilterOn(item, age, idleBusinessDays(item, now));

// Whole calendar days since the last activity, e.g. "5d" (stays in days past a week). Calendar days, not business days,
// so it reconciles with the dates shown beside it.
export const formatActionItemIdleAge = (item: Pick<api.VendorActionItemResponse, 'lastActivityAt'>, now: DateTime = DateTime.now()): string => (
  `${Math.floor(idleDays(item, now))}d`
);

// A stale item's tooltip, in whole business days (what the staleness is rated on), e.g. "No activity for 4 business days".
export const actionItemStalenessTitle = ({ businessDays }: ActionItemStaleAge): string => (
  `No activity for ${Math.floor(businessDays)} business days`
);

// Emails compare case-insensitively.
export const isAssignedTo = (item: Pick<api.VendorActionItemResponse, 'assignedTo'>, email?: string): boolean => (
  !!email && item.assignedTo?.toLowerCase() === email.toLowerCase()
);

export type ActionItemPermissions = {
  readonly isReadOnly: boolean;
  readonly isVendorManagement: boolean;
};

// Creating, editing, moving and noting Action Items needs vendor management; read-only operators can only look.
export const canWriteActionItems = ({ isReadOnly, isVendorManagement }: ActionItemPermissions): boolean => !isReadOnly && isVendorManagement;

// Card/section heading; without open items it says so (the empty table is left out).
export const actionItemsHeading = (openCount: number, isLoading: boolean): string => {
  if (isLoading) return 'Action Items';
  return openCount === 0 ? 'No Vendor Action Items' : `Action Items (${openCount} open)`;
};

// An owner/assignee name taken from their email: the local part as capitalized words, e.g. "jane.doe" → "Jane Doe".
export const formatAssigneeEmail = (email: string): string => formatOperatorEmail(email)
  .split(/[._-]+/)
  .filter(Boolean)
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(' ');

export const formatAssignee = (email?: string): string => (email ? formatAssigneeEmail(email) : 'Unassigned');

export type AssigneeOption = { readonly value: string; readonly label: string };

export const sameEmail = (a: string, b?: string): boolean => !!b && a.toLowerCase() === b.toLowerCase();

// An operator's full name for pickers, or their email's local part if they have no name.
export const formatOperatorName = ({ email, firstName, lastName }: api.VendorActionItemAssigneeResponse): string => (
  `${firstName} ${lastName}`.trim() || formatAssigneeEmail(email)
);

// Assignee picker choices: the viewer first, marked "(me)", then the other operators by name, in the given order. Emails
// match case-insensitively (the signed-in email and the operator list can differ in case); the viewer's option keeps the
// viewer's spelling so it matches a draft defaulted to `me`. A current assignee who is no longer an operator stays
// listed, by email, so the picker never silently shows a different value.
export const assigneeOptions = (
  operators: readonly api.VendorActionItemAssigneeResponse[],
  me?: string,
  current?: string,
): readonly AssigneeOption[] => {
  const meOperator = operators.find((operator) => sameEmail(operator.email, me));
  const others = operators.filter((operator) => operator !== meOperator);
  const departed = current && !sameEmail(current, me) && !operators.some((operator) => sameEmail(operator.email, current)) ? [current] : [];
  return [
    ...(me ? [{ value: me, label: `${meOperator ? formatOperatorName(meOperator) : formatAssigneeEmail(me)} (me)` }] : []),
    ...departed.map((email) => ({ value: email, label: formatAssigneeEmail(email) })),
    ...others.map((operator) => ({ value: operator.email, label: formatOperatorName(operator) })),
  ];
};

// Newest first by creation, which status changes and Notes don't move, so a list sorted this way holds still as it's
// worked through.
export const byCreatedDesc = (a: api.VendorActionItemResponse, b: api.VendorActionItemResponse): number => (
  b.createdAt.localeCompare(a.createdAt)
);

// Newest activity first.
export const byLastActivityDesc = (a: api.VendorActionItemResponse, b: api.VendorActionItemResponse): number => (
  a.lastActivityAt < b.lastActivityAt ? 1 : -1
);

export type RelatedActionItems = {
  readonly open: readonly api.VendorActionItemResponse[];
  readonly closed: readonly api.VendorActionItemResponse[];
};

// The Vendor's items other than `currentId` (all of them without one), for the detail drawer and the create form, newest created first (the table's order): every open one, and every
// closed one for "Show closed".
export const relatedActionItems = (items: readonly api.VendorActionItemResponse[], currentId?: string): RelatedActionItems => {
  const others = items.filter((item) => item.id !== currentId).sort(byCreatedDesc);
  return {
    open: others.filter(isOpenActionItem),
    closed: others.filter((item) => !isOpenActionItem(item)),
  };
};

export type ActionItemGroup = { readonly status: api.VendorActionItemStatus; readonly items: readonly api.VendorActionItemResponse[] };

// Items grouped by the given statuses, in that order, newest activity first, omitting empty groups.
export const groupByStatus = (
  items: readonly api.VendorActionItemResponse[],
  statuses: readonly api.VendorActionItemStatus[],
): readonly ActionItemGroup[] => (
  statuses
    .map((status) => ({ status, items: items.filter((item) => item.status === status).sort(byLastActivityDesc) }))
    .filter((group) => group.items.length > 0)
);

// One line of timeline text for an audit-style entry (notes render separately).
export const timelineEntryText = (entry: api.VendorActionItemTimelineEntry): string => {
  switch (entry.kind) {
    case api.VendorActionItemTimelineEntryKind.CREATED:
      return entry.sourceNote ? 'Created from a Note' : 'Created';
    case api.VendorActionItemTimelineEntryKind.STATUS_CHANGED:
      return `Status: ${entry.from ? actionItemStatusLabel(entry.from) : '—'} → ${entry.to ? actionItemStatusLabel(entry.to) : '—'}`;
    case api.VendorActionItemTimelineEntryKind.REASSIGNED:
      return `Assignee: ${formatAssignee(entry.from)} → ${formatAssignee(entry.to)}`;
    case api.VendorActionItemTimelineEntryKind.EDITED:
      return `Edited the ${entry.field ?? 'item'}`;
    default:
      return '';
  }
};

// An empty side means the value was blank (the API omits empty values).
export type TimelineEntryChange = { readonly before?: string; readonly after?: string };

// The before/after values to expand under an entry, for kinds whose one line doesn't already show the change (edits).
export const timelineEntryChange = (entry: api.VendorActionItemTimelineEntry): TimelineEntryChange | undefined => (
  entry.kind === api.VendorActionItemTimelineEntryKind.EDITED ? { before: entry.from, after: entry.to } : undefined
);

// Which timeline entries the detail drawer shows: everything, or only Notes.
export type ActionItemTimelineView = 'all' | 'comments';

// The detail drawer's lower section: the full timeline, the item's comments with the Vendor's Notes and other Action
// Items, or the Vendor's Enablement summary.
export type ActionItemDetailView = 'all' | 'context' | 'vendorSummary';

export const ActionItemDetailViewOptions: readonly { readonly value: ActionItemDetailView; readonly label: string }[] = [
  { value: 'all', label: 'Timeline' },
  { value: 'context', label: 'Context' },
  { value: 'vendorSummary', label: 'Vendor Status' },
];

// The entries for a view, newest first. The sort is stable, so same-instant entries keep the API's order.
export const timelineView = (
  entries: readonly api.VendorActionItemTimelineEntry[],
  view: ActionItemTimelineView,
): readonly api.VendorActionItemTimelineEntry[] => (
  entries
    .filter((entry) => view === 'all' || entry.kind === api.VendorActionItemTimelineEntryKind.NOTE)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
);

// ---- Action Items table sorting ----------------------------------------------------------------------------------
// Ascending comparators for the table's client-side column sorts; the table flips them for descending.

// Lifecycle order: To Do, In Progress, Waiting, Done, Canceled.
export const compareActionItemStatus = (a: api.VendorActionItemResponse, b: api.VendorActionItemResponse): number => (
  ActionItemStatuses.indexOf(a.status) - ActionItemStatuses.indexOf(b.status)
);

// By owner name; unassigned items sort after assigned ones.
export const compareActionItemOwner = (a: api.VendorActionItemResponse, b: api.VendorActionItemResponse): number => {
  if (!a.assignedTo || !b.assignedTo) return Number(!a.assignedTo) - Number(!b.assignedTo);
  return formatAssigneeEmail(a.assignedTo).localeCompare(formatAssigneeEmail(b.assignedTo));
};

// By Vendor name; items without one sort last.
export const compareActionItemVendor = (a: api.VendorActionItemResponse, b: api.VendorActionItemResponse): number => {
  if (!a.vendorName || !b.vendorName) return Number(!a.vendorName) - Number(!b.vendorName);
  return a.vendorName.localeCompare(b.vendorName);
};

// The table's unsorted order: open items, then closed, each newest created first.
export const actionItemsTableOrder = (items: readonly api.VendorActionItemResponse[]): readonly api.VendorActionItemResponse[] => (
  [...items].sort((a, b) => Number(!isOpenActionItem(a)) - Number(!isOpenActionItem(b)) || byCreatedDesc(a, b))
);

// Oldest first.
export const compareActionItemCreated = (a: api.VendorActionItemResponse, b: api.VendorActionItemResponse): number => (
  a.createdAt.localeCompare(b.createdAt)
);

// ---- Action Items board -------------------------------------------------------------------------------------------

// Whose items the board shows: the viewer's, everyone's, nobody's, or one Operator's (their email).
export type ActionItemsScope = 'mine' | 'all' | 'unassigned' | string;

// The group scopes; Unassigned and single Operators share the assignee picker beside them.
export const ActionItemsScopes: readonly { readonly value: ActionItemsScope; readonly label: string }[] = [
  { value: 'all', label: 'Everyone' },
  { value: 'mine', label: 'Me' },
];

const GroupScopes: readonly ActionItemsScope[] = ['all', 'mine', 'unassigned'];

// A scope naming one Operator rather than a group.
export const isOperatorScope = (scope: ActionItemsScope): boolean => !GroupScopes.includes(scope);

// The scope that shows one assignee's items: Mine for the viewer, Unassigned for nobody, otherwise that Operator.
export const assigneeScope = (assignee?: string, me?: string): ActionItemsScope => {
  if (!assignee) return 'unassigned';
  return sameEmail(assignee, me) ? 'mine' : assignee;
};

// How far back the Done column reaches; `hidden` drops closed items, and with them the Done column.
export type ActionItemsDoneWindow = '7d' | '14d' | '30d' | 'all' | 'hidden';

export const ActionItemsDoneWindowOptions: readonly { readonly value: ActionItemsDoneWindow; readonly label: string; readonly dividerAfter?: boolean }[] = [
  { value: 'hidden', label: 'Hidden', dividerAfter: true },
  { value: '7d', label: 'Last 7 days' },
  { value: '14d', label: 'Last 14 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'All' },
];

const DoneWindowDays: Readonly<Record<Exclude<ActionItemsDoneWindow, 'all' | 'hidden'>, number>> = { '7d': 7, '14d': 14, '30d': 30 };

export const showsClosedItems = (filters: Pick<ActionItemsBoardFilters, 'doneWindow'>): boolean => filters.doneWindow !== 'hidden';

export type ActionItemsBoardFilters = {
  // Applied client-side (see matchesScope), so the pills can count every assignee from the board's one request.
  readonly scope: ActionItemsScope;
  readonly vendorId?: string;
  readonly includeCanceled: boolean;
  readonly doneWindow: ActionItemsDoneWindow;
  // Applied client-side (see matchesPartnerFilter); not sent to the API yet.
  readonly partnerIds: readonly api.Partners[];
  // Stale or recent items only (see matchesAgeFilter); applied client-side.
  readonly age: ActionItemsAgeFilter;
};

// The viewer's open items, shared by the board header and the nav badge so both read one cache entry.
export const MyOpenActionItemsParams: api.VendorActionItemFilterParams = {
  status: api.OpenVendorActionItemStatuses,
  assignedTo: api.VendorActionItemAssigneeFilter.ME,
};

// The board's list request: everyone's items (the assignee scope is applied client-side). The Done window starts at the beginning of a day so the request (and its cache entry)
// stays the same across renders.
export const actionItemsBoardParams = (filters: ActionItemsBoardFilters, now: DateTime = DateTime.now()): api.VendorActionItemFilterParams => {
  const vendorId = filters.vendorId as api.UUID | undefined;
  if (filters.doneWindow === 'hidden') return { status: api.OpenVendorActionItemStatuses, vendorId };
  return {
    // Omitting status returns everything except canceled.
    status: filters.includeCanceled ? ActionItemStatuses : undefined,
    vendorId,
    closedSince: filters.doneWindow === 'all'
      ? undefined
      : now.minus({ days: DoneWindowDays[filters.doneWindow] }).startOf('day').toUTC().toISO() as api.ISODateString,
  };
};

export type VendorOption = { readonly value: string; readonly label: string };

// Distinct Vendors among the items, by name.
export const vendorOptions = (items: readonly api.VendorActionItemResponse[]): readonly VendorOption[] => (
  [...new Map(items.map((item) => [item.vendorId, item.vendorName ?? item.vendorId])).entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
);

// The Vendor filter's options, plus the picked Vendor when they lack it (e.g. a deep link to a Vendor outside the
// assignee scope), labeled from its items or else by id.
export const withPickedVendor = (
  options: readonly VendorOption[],
  vendorId: string | undefined,
  items: readonly api.VendorActionItemResponse[],
): readonly VendorOption[] => {
  if (!vendorId || options.some((option) => option.value === vendorId)) return options;
  const label = items.find((item) => item.vendorId === vendorId)?.vendorName ?? vendorId;
  return [...options, { value: vendorId, label }];
};

// Whether a keydown in an entry form's text area posts: Enter does; Shift+Enter adds a line. Enter that confirms an IME
// composition does neither; Safari has already ended the composition by then, so keyCode 229 catches it there.
export const isEntrySubmitKey = (e: { readonly key: string; readonly shiftKey: boolean; readonly isComposing: boolean; readonly keyCode: number }): boolean => (
  e.key === 'Enter' && !e.shiftKey && !e.isComposing && e.keyCode !== 229
);

// Board columns, in lifecycle order; canceled items sit in Done.
export const ActionItemBoardStatuses: readonly api.VendorActionItemStatus[] = [
  api.VendorActionItemStatus.TODO,
  api.VendorActionItemStatus.IN_PROGRESS,
  api.VendorActionItemStatus.WAITING,
  api.VendorActionItemStatus.DONE,
];

export const boardColumnStatus = (status: api.VendorActionItemStatus): api.VendorActionItemStatus => (
  status === api.VendorActionItemStatus.CANCELED ? api.VendorActionItemStatus.DONE : status
);

// The board's columns: without Done while closed items are hidden.
export const boardColumnStatuses = (showClosed: boolean): readonly api.VendorActionItemStatus[] => (
  showClosed ? ActionItemBoardStatuses : ActionItemBoardStatuses.filter((status) => status !== api.VendorActionItemStatus.DONE)
);

// Every column, empty ones included, newest activity first. Items outside the given columns are left out.
export const boardColumns = (
  items: readonly api.VendorActionItemResponse[],
  statuses: readonly api.VendorActionItemStatus[] = ActionItemBoardStatuses,
): readonly ActionItemGroup[] => (
  statuses.map((status) => ({
    status,
    items: items.filter((item) => boardColumnStatus(item.status) === status).sort(byLastActivityDesc),
  }))
);

// How many cards a Done column shows before "Show more", so a long history doesn't mount every closed card.
export const DoneColumnLimit = 10;

// A column's cards as shown: the Done column stops at `limit` (newest first) unless expanded; open columns show all.
export const visibleColumnItems = (
  { status, items }: ActionItemGroup,
  limit: number,
  expanded: boolean,
): { readonly shown: readonly api.VendorActionItemResponse[]; readonly hidden: number } => {
  if (status !== api.VendorActionItemStatus.DONE || expanded) return { shown: items, hidden: 0 };
  return { shown: items.slice(0, limit), hidden: Math.max(items.length - limit, 0) };
};

export const openActionItemCount = (items: readonly api.VendorActionItemResponse[]): number => items.filter(isOpenActionItem).length;

export type ActionItemsGroupBy = 'vendor' | 'assignee' | 'partner' | 'none';

export const ActionItemsGroupByOptions: readonly { readonly value: ActionItemsGroupBy; readonly label: string }[] = [
  { value: 'assignee', label: 'Assignee' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'partner', label: 'Partner' },
  { value: 'none', label: 'None' },
];

// The board page's layout: status columns per lane, or a table per lane.
export type ActionItemsLayout = 'board' | 'table';

export type BoardLane = {
  // Unique per lane; drags only land in the lane they started in.
  readonly key: string;
  readonly items: readonly api.VendorActionItemResponse[];
} & (
  | { readonly kind: 'vendor'; readonly vendorId: string; readonly vendorName: string }
  | { readonly kind: 'assignee'; readonly assignee?: string; readonly isMe: boolean }
  // No partnerId: the Vendor has no program.
  | { readonly kind: 'partner'; readonly partnerId?: api.Partners }
  | { readonly kind: 'all' }
);

const UnassignedLaneKey = 'unassigned';

// One lane per Vendor with items, most open items first, then by name.
export const vendorLanes = (items: readonly api.VendorActionItemResponse[]): readonly BoardLane[] => {
  const byVendor = items.reduce((lanes, item) => lanes.set(item.vendorId, [...(lanes.get(item.vendorId) ?? []), item]), new Map<string, api.VendorActionItemResponse[]>());
  return [...byVendor.entries()]
    .map(([vendorId, laneItems]) => ({
      kind: 'vendor' as const, key: vendorId, vendorId, vendorName: laneItems[0].vendorName ?? vendorId, items: laneItems,
    }))
    .sort((a, b) => openActionItemCount(b.items) - openActionItemCount(a.items) || a.vendorName.localeCompare(b.vendorName));
};

// One lane per assignee with items: the viewer first, then by email, Unassigned last. Emails match case-insensitively.
export const assigneeLanes = (items: readonly api.VendorActionItemResponse[], me?: string): readonly BoardLane[] => {
  const byAssignee = items.reduce((lanes, item) => {
    const key = item.assignedTo?.toLowerCase() ?? UnassignedLaneKey;
    const lane = lanes.get(key);
    return lanes.set(key, { assignee: lane?.assignee ?? item.assignedTo, items: [...(lane?.items ?? []), item] });
  }, new Map<string, { assignee?: string; items: api.VendorActionItemResponse[] }>());
  const rank = (lane: BoardLane & { kind: 'assignee' }) => {
    if (lane.isMe) return 0;
    return lane.assignee ? 1 : 2;
  };
  return [...byAssignee.entries()]
    .map(([key, lane]) => ({
      kind: 'assignee' as const, key, assignee: lane.assignee, isMe: sameEmail(key, me), items: lane.items,
    }))
    .sort((a, b) => rank(a) - rank(b) || a.key.localeCompare(b.key));
};

// One lane per partner with items, in partner order. An item sits only in the lane of its newest program's partner;
// without a program, in the last "no partner" lane.
export const partnerLanes = (items: readonly api.VendorActionItemResponse[]): readonly BoardLane[] => {
  const lanes = items.reduce((byKey, item) => {
    const partnerId = primaryProgram(item)?.partnerId;
    const key = `partner:${partnerId ?? 'none'}`;
    return byKey.set(key, { kind: 'partner', partnerId, key, items: [...(byKey.get(key)?.items ?? []), item] });
  }, new Map<string, Extract<BoardLane, { readonly kind: 'partner' }>>());
  return [...lanes.values()].sort((a, b) => comparePartnerIds(a.partnerId, b.partnerId));
};

export const boardLanes = (items: readonly api.VendorActionItemResponse[], groupBy: ActionItemsGroupBy, me?: string): readonly BoardLane[] => {
  switch (groupBy) {
    case 'vendor':
      return vendorLanes(items);
    case 'assignee':
      return assigneeLanes(items, me);
    case 'partner':
      return partnerLanes(items);
    default:
      return [{ kind: 'all', key: 'all', items }];
  }
};

// Distinct assignees of a lane's open items (Unassigned included), for the lane header.
export const openAssignees = (items: readonly api.VendorActionItemResponse[]): readonly (string | undefined)[] => (
  [...new Map(items.filter(isOpenActionItem).map((item) => [item.assignedTo?.toLowerCase(), item.assignedTo])).values()]
);

export type BoardDrop = {
  readonly status: api.VendorActionItemStatus;
  readonly fromLane: string;
  readonly toLane: string;
  readonly toStatus: api.VendorActionItemStatus;
};

// The status a drop sets, or undefined when it changes nothing: drags only move status, never across lanes (which
// would change the Vendor or assignee), and dropping back on the item's own column (e.g. a canceled item onto Done)
// is a no-op.
export const boardDropStatus = ({
  status, fromLane, toLane, toStatus,
}: BoardDrop): api.VendorActionItemStatus | undefined => (
  fromLane === toLane && boardColumnStatus(status) !== toStatus ? toStatus : undefined
);

// Screen-reader announcements for board drags.
export const BoardDragInstructions = 'To move this Action Item to another status, press Space to pick it up, use the arrow keys to move between columns, '
  + 'then press Space to drop it or Escape to cancel. Press Enter to open it. The "Move to…" menu changes status without dragging.';

export const boardDragStartAnnouncement = (title: string, status: api.VendorActionItemStatus): string => (
  `Picked up ${title} from ${actionItemStatusLabel(boardColumnStatus(status))}.`
);

export const boardDragOverAnnouncement = (title: string, target?: { readonly sameLane: boolean; readonly status: api.VendorActionItemStatus }): string => {
  if (!target) return `${title} is not over a column.`;
  if (!target.sameLane) return `${title} can only move within its own lane.`;
  return `${title} is over ${actionItemStatusLabel(target.status)}.`;
};

export const boardDragEndAnnouncement = (title: string, status: api.VendorActionItemStatus, movedTo?: api.VendorActionItemStatus): string => (
  movedTo
    ? `Moved ${title} to ${actionItemStatusLabel(movedTo)}.`
    : `${title} stays in ${actionItemStatusLabel(boardColumnStatus(status))}.`
);

// Vendor pages, including a partner's Vendor service agreements. The partner `internal-vendors` / `external-vendors`
// pages are External Vendors, not Vendors.
const VendorPagePaths = ['/vendors/:vendorId/*', '/partners/:partnerId/vendors/:vendorId/*'];

// The Vendor the current page is about, from its path or a `?vendorId=` deep link, to prefill a new Action Item.
export const pageVendorId = (pathname: string, search: string): string | undefined => (
  VendorPagePaths.map((path) => matchPath(path, pathname)?.params.vendorId).find(Boolean)
  ?? new URLSearchParams(search).get('vendorId')
  ?? undefined
);

// The board's assignee scope, applied client-side.
export const matchesScope = (item: Pick<api.VendorActionItemResponse, 'assignedTo'>, scope: ActionItemsScope, me?: string): boolean => {
  switch (scope) {
    case 'all':
      return true;
    case 'mine':
      return isAssignedTo(item, me);
    case 'unassigned':
      return !item.assignedTo;
    default:
      return sameEmail(scope, item.assignedTo);
  }
};

export type ActionItemFilterCounts = {
  readonly scope: (scope: ActionItemsScope) => number;
  readonly partner: (partnerIds: readonly api.Partners[]) => number;
  readonly age: (age: ActionItemsAgeFilter) => number;
};

// Counts for the filter pills' options. Each category counts the items matching every other filter, so an option's
// count is how many items picking it would show. `items` is the board's request.
export const actionItemFilterCounts = (
  items: readonly api.VendorActionItemResponse[],
  filters: Pick<ActionItemsBoardFilters, 'scope' | 'partnerIds' | 'age'>,
  me?: string,
  now?: DateTime,
): ActionItemFilterCounts => {
  // Each item's idle business days, counted once for every Age check below.
  const at = now ?? DateTime.now();
  const idle = new Map(items.map((item) => [item, idleBusinessDays(item, at)]));
  const agedAs = (item: api.VendorActionItemResponse, age: ActionItemsAgeFilter) => matchesAgeFilterOn(item, age, idle.get(item) ?? 0);
  const scoped = (item: api.VendorActionItemResponse) => matchesScope(item, filters.scope, me);
  const partnered = (item: api.VendorActionItemResponse) => matchesPartnerFilter(item, filters.partnerIds);
  const aged = (item: api.VendorActionItemResponse) => agedAs(item, filters.age);
  const forScope = items.filter((item) => partnered(item) && aged(item));
  const forPartner = items.filter((item) => scoped(item) && aged(item));
  const forAge = items.filter((item) => scoped(item) && partnered(item));
  return {
    scope: (scope) => forScope.filter((item) => matchesScope(item, scope, me)).length,
    partner: (partnerIds) => forPartner.filter((item) => matchesPartnerFilter(item, partnerIds)).length,
    age: (age) => forAge.filter((item) => agedAs(item, age)).length,
  };
};
